import React, { useState } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { readResponse } from '../utils/fetchSafe';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') || '';
  const email = searchParams.get('email') || '';
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, email, password })
      });
      const { data } = await readResponse(res);
      if (!res.ok) throw new Error(data.detail || 'Reset failed');
      toast.success('Password reset successfully!');
      navigate('/');
    } catch (err) {
      toast.error(err.message || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  if (!token || !email) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="bg-white p-8 rounded shadow max-w-md text-center">
          <h2 className="text-xl text-gray-700 mb-4">Invalid Reset Link</h2>
          <p className="text-gray-500 mb-6">This password reset link is invalid or has expired.</p>
          <Link to="/forgot-password" className="text-blue-600 hover:underline">Request a new reset link</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex">
      <div
        className="flex-1 bg-cover bg-center"
        style={{ backgroundImage: 'url("https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=1200&q=80")' }}
      />
      <div className="w-full max-w-md bg-white flex flex-col justify-center px-12">
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-12">
            <span className="text-gray-600 text-4xl font-light">resto</span>
            <div className="w-16 h-16 rounded-full bg-[#FFA94D] flex items-center justify-center">
              <span className="text-white text-xl font-semibold">mp</span>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <h2 className="text-2xl text-gray-700 font-light">Reset Password</h2>
          <p className="text-gray-500 text-sm">Enter your new password below.</p>
          <div>
            <input
              type="password"
              placeholder="New Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#FFA94D] focus:border-transparent text-gray-700"
              required
              data-testid="reset-password-input"
            />
          </div>
          <div>
            <input
              type="password"
              placeholder="Confirm Password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#FFA94D] focus:border-transparent text-gray-700"
              required
              data-testid="reset-confirm-input"
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-[#FFA94D] hover:bg-[#F08A2A] text-white py-3 rounded font-medium transition-colors disabled:opacity-50"
            data-testid="reset-submit-btn"
          >
            {loading ? 'Resetting...' : 'Reset Password'}
          </button>
          <Link to="/" className="inline-block text-sm text-blue-600 hover:underline">
            Back to login
          </Link>
        </form>
      </div>
    </div>
  );
};

export default ResetPassword;
